import partition from 'lodash/partition';

import {
  ESC,
  FS,
  initializePrinter,
  LF,
  printAndLineFeed,
  printAndNewLineCommands,
  printCommands,
  selectCodeConversion,
  specialUnicodeEncoding,
} from '../parser/commands';
import { PrinterContext } from '../renderer/printer';
import { encodeTextData } from '../renderer/text';
import { walkAST } from '../renderer/util';
import { AnalyzerGuide, AnalyzerNode, AstNode, CommandByteSequence, DataChunk, Description, RuleTree, TableChunk } from '../types';

const maxSameCharacterSequence = (textData: string) => {
  let lastByte = '';
  const countArr = [];
  let count = 0;

  for (const char of textData) {
    if (lastByte !== char) {
      lastByte = char;
      countArr.push(count);
      count = 0;
    } else count += 1;
  }

  countArr.push(count);
  return Math.max(...countArr);
};

const findCommand = (command: CommandByteSequence[], ast: AstNode[]) => {
  for (const [index, node] of ast.entries()) {
    if (command.includes(node.command)) {
      return index;
    }
  }

  return -1;
};

const isPrintingCommand = (command: CommandByteSequence) => {
  return printCommands.includes(command);
};

export const isPrintingWithLineFeedCommand = (command: CommandByteSequence) => {
  return printAndNewLineCommands.includes(command);
};

const addMissingInitializerCommand = (ast: AstNode[], commandTree: RuleTree) => {
  const correctedAST: AstNode[] = [];
  if (ast[0].command !== initializePrinter) {
    correctedAST.push({
      command: initializePrinter,
      // initializePrinter command
      description: (
        commandTree[ESC]['\x40'] as {
          content: Description;
        }
      ).content.description,
      isCorrected: true,
    });
  }

  correctedAST.push(...ast);
  return correctedAST;
};

// This effectively remove "top padding" of the receipt
const removeAllBeginningPrintWithLineFeedCommand = (ast: AstNode[]) => {
  const correctedAST: AstNode[] = [];
  let possibleTitleTextFound = false;

  const printerContext = new PrinterContext();
  for (const [, node, textData] of walkAST(ast, printerContext)) {
    if (!possibleTitleTextFound && isPrintingWithLineFeedCommand(node.command) && textData.length > 0) {
      possibleTitleTextFound = true;
    }

    if (!isPrintingWithLineFeedCommand(node.command) || possibleTitleTextFound) {
      correctedAST.push(node);
    }
  }

  return correctedAST;
};

const fixTextDataPrintCommand = (ast: AstNode[], commandTree: RuleTree) => {
  const correctedAST: AstNode[] = [];
  const skipIndex: number[] = [];

  for (const [index, node] of ast.entries()) {
    if (skipIndex.includes(index)) continue;
    correctedAST.push(node);

    if (node.extractedData?.text?.length > 0 && !isPrintingCommand(node.command)) {
      let correctedChunk: AstNode = {
        command: printAndLineFeed,
        description: (
          commandTree[LF] as {
            content: Description;
          }
        ).content.description,
      };

      const maybeFoundIndex = findCommand(printCommands, ast.slice(index - 1));
      if (maybeFoundIndex >= 0) {
        const printCommand = ast[index - 1 + maybeFoundIndex];
        if (!printCommand.extractedData?.text || printCommand.extractedData?.text.length === 0) {
          correctedChunk = printCommand;
          skipIndex.push(index - 1 + maybeFoundIndex);
        }
      }

      correctedChunk = {
        ...correctedChunk,
        isCorrected: true,
        extractedData: {
          text: node.extractedData.text,
        },
      };

      // This is essentially moving the extracted data text to another chunk
      // which is the corrected chunk
      delete correctedAST[correctedAST.length - 1].extractedData.text;
      correctedAST.splice(correctedAST.length - 1, 0, correctedChunk);
    }
  }

  return correctedAST;
};

const mergeSpecialUnicodeCommand = (ast: AstNode[], commandTree: RuleTree) => {
  const correctedAST: AstNode[] = [];

  let tempMergedSpecialUnicodeChunk: AstNode = null;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  for (const [index, node] of ast.entries()) {
    if (node.command === specialUnicodeEncoding) {
      if (!tempMergedSpecialUnicodeChunk) {
        tempMergedSpecialUnicodeChunk = node;
      }

      if (node.extractedData?.text.join('') !== tempMergedSpecialUnicodeChunk.extractedData.text.join('')) {
        tempMergedSpecialUnicodeChunk.extractedData.text.push(...(node.extractedData?.text ?? []));
      }

      // Get unicode bytes from parsedArgs (please note that we dont care about the last byte since it will always be \x00)
      const unicodeBytes = (node.parsedArgs[node.parsedArgs.length - 1] as number[]).slice(0, -1).map((val) => String.fromCharCode(val));
      tempMergedSpecialUnicodeChunk.extractedData.text.push(...unicodeBytes);
    } else {
      if (tempMergedSpecialUnicodeChunk) {
        if (correctedAST[correctedAST.length - 1].command === selectCodeConversion && correctedAST[correctedAST.length - 1].parsedArgs.slice(-1)[0] === 1) {
          correctedAST.pop();
        } else {
          // Inject FS ( C unicode command
          correctedAST.push({
            command: selectCodeConversion,
            // selectCodeConversion command
            description: (
              commandTree[FS]['\x28']['\x43'] as {
                content: Description;
              }
            ).content.description,
            parsedArgs: [0, 0, 0, 2], // We dont care about the first couple of arguments
            isCorrected: true,
          });
        }

        if (node.extractedData?.text) {
          node.extractedData.text.unshift(...tempMergedSpecialUnicodeChunk.extractedData.text);
        } else {
          node.extractedData.text = tempMergedSpecialUnicodeChunk.extractedData.text;
        }
      }

      correctedAST.push(node);

      if (tempMergedSpecialUnicodeChunk) {
        // Inject FS ( C codepage command
        correctedAST.push({
          command: selectCodeConversion,
          // selectCodeConversion command
          description: (
            commandTree[FS]['\x28']['\x43'] as {
              content: Description;
            }
          ).content.description,
          parsedArgs: [0, 0, 0, 1], // We dont care about the first couple of arguments
          isCorrected: true,
        });
      }

      tempMergedSpecialUnicodeChunk = null;
    }
  }

  return correctedAST;
};

const splitOverflowTextData = (ast: AstNode[], maximumCharacterInOneline: number) => {
  const printerContext = new PrinterContext();
  for (const [index, node, textData] of walkAST(ast, printerContext)) {
    if (isPrintingWithLineFeedCommand(node.command) && textData.length > maximumCharacterInOneline) {
      // eslint-disable-next-line security/detect-non-literal-regexp
      const leastPossibleCharacters = new RegExp(`[\\s\\S]{1,${maximumCharacterInOneline}}`, 'g');
      const newChunkDatas: AstNode[] = [];

      textData.match(leastPossibleCharacters).forEach((newTextData) => {
        newChunkDatas.push({
          ...node,
          isCorrected: true,
          extractedData: {
            text: encodeTextData(newTextData, printerContext.getTextState().encoding === 'codepage' ? printerContext.getTextState().codepage : 'unicode'),
          },
        });
      });

      ast.splice(index, 1, ...newChunkDatas);
    }
  }

  return ast;
};

const findPossibleMaximumWidth = (ast: AstNode[]) => {
  const printerContext = new PrinterContext();

  const charLengths = [];
  for (const [, node, textData] of walkAST(ast, printerContext)) {
    if (
      textData.length > 0 && // Text data is presumably only around 0 - 60 characters in a single line
      textData.length < 60 &&
      isPrintingWithLineFeedCommand(node.command) && // Make sure its a print with line feed command
      printerContext.getTextState().position === 'left' && // Make sure there were no align command previously
      printerContext.getTextState().height === 1 && // Make sure the text height was not altered previously
      printerContext.getTextState().width === 1 && // Make sure the text width was not altered previously
      possiblyAlignedText(textData) // Make sure that this is some sort of aligned text
    ) {
      // Possible maximum character length found
      charLengths.push(textData.length);
    }
  }

  if (charLengths.length > 0) {
    return Math.max(...charLengths);
  }

  // No possible maximum character length found
  return -1;
};

/**
 * Check if certain text data is actually an aligned text via semantic analysis
 * which works by checking the maximum number of how many characters are the same
 * in the corresponding text data, for example these text datas will be counted as
 * aligned text when the threshold is set to 10:
 *
 * "                test              "
 * "              pi-xcels            "
 * "----------------------------------"
 * "                 ^v^              "
 *
 * And 10 is a logical default value here because average actual maximum characters
 * in a pos terminal printer is 32 chars, so having ~30% of the average as default
 * value is cool enough to determine an aligned text in most cases
 */
const possiblyAlignedText = (textData: string, threshold = 10) => {
  return maxSameCharacterSequence(textData) > threshold;
};

// Text is assumed as divider text for when it has all same characters in the string
export const possiblyDividerText = (textData: string, maximumCharacterInOneline: number) => {
  if (textData.split('').every((char) => char === textData[0])) return true;
  else return maximumCharacterInOneline > 0 && maxSameCharacterSequence(textData) > maximumCharacterInOneline - 5;
};

export const fixParsedEscPosAST = (ast: AstNode[], commandTree: RuleTree) => {
  let correctedAST: AstNode[] = ast;
  correctedAST = addMissingInitializerCommand(correctedAST, commandTree);
  correctedAST = mergeSpecialUnicodeCommand(correctedAST, commandTree);
  correctedAST = fixTextDataPrintCommand(correctedAST, commandTree);

  // TODO: Add a width config is better than this but this is also very handy in most cases
  //       so put a check here later that if a config exist then use config, if not, then use
  //       this one
  const possibleMaximumWidth = findPossibleMaximumWidth(correctedAST);
  if (possibleMaximumWidth > 0) {
    correctedAST = splitOverflowTextData(correctedAST, possibleMaximumWidth);
  }

  correctedAST = removeAllBeginningPrintWithLineFeedCommand(correctedAST);

  return {
    ast: correctedAST,
    possibleMaxWidth: possibleMaximumWidth,
  };
};

export class AnalyzerManager {
  #analyzerGuide: AnalyzerGuide;
  #ast: AstNode[];
  #possibleMaxWidth: number;
  #lineNumber: number;
  #parsedChunks: number;
  #parsedObjects: Record<string, any>;
  #unparsedTexts: {
    text: string;
    lineNumber: number;
  }[];

  constructor(analyzerGuide: AnalyzerGuide, ast: AstNode[], possibleMaxWidth: number, lineNumber = 0) {
    this.#analyzerGuide = analyzerGuide;
    this.#ast = ast;
    this.#possibleMaxWidth = possibleMaxWidth;
    this.#lineNumber = lineNumber;

    this.#parsedChunks = 0;
    this.#parsedObjects = {};
    this.#unparsedTexts = [];
  }

  get parsedObjects() {
    return this.#parsedObjects;
  }

  get unparsedTexts() {
    return this.#unparsedTexts;
  }

  get #getCurrentAnalyzerNode() {
    if (this.#parsedChunks >= this.#analyzerGuide.length) return null;
    else return this.#analyzerGuide[this.#parsedChunks];
  }

  get #partitionedAnalyzerGuide() {
    return partition(
      this.#analyzerGuide.map((node, index) => {
        return {
          node,
          index,
        };
      }),
      (object) => {
        if (Array.isArray(object.node)) {
          return object.node.some((childNode) => childNode.isFixed);
        } else return object.node.isFixed;
      }
    );
  }

  incrementLineNumber() {
    this.#lineNumber += 1;
  }

  #splitReliableAnalyzerNodes(
    analyzerGuide: {
      node: AnalyzerNode;
      index: number;
    }[]
  ) {
    return partition(analyzerGuide, (object) => {
      if (Array.isArray(object.node)) {
        return object.node.some((childNode) => {
          return 'dataType' in childNode || 'dataRegex' in childNode;
        });
      } else {
        if (object.node.chunkType === 'dataChunk') {
          return 'dataType' in object.node || 'dataRegex' in object.node;
        }
      }
    });
  }

  #splitArrayAnalyzerNodes(
    analyzerGuide: {
      node: AnalyzerNode;
      index: number;
    }[]
  ) {
    return partition(analyzerGuide, (object) => Array.isArray(object.node));
  }

  extractDataFromTextDynamically(textData: string, astIndex: number) {
    // Shortcircuit when analyzerGuide is empty which means there are
    // no analyzerGuide left to be used for parsing the textData
    if (this.#analyzerGuide.length === 0) {
      return -1;
    }

    let isParsed = false;
    const [fixedNodes, dynamicNodes] = this.#partitionedAnalyzerGuide;
    const [reliableNodes, unreliableNodes] = this.#splitReliableAnalyzerNodes(dynamicNodes);
    const [arrayNodes, singleNodes] = this.#splitArrayAnalyzerNodes(unreliableNodes);

    // Check if textData could be parsed with a reliableNode
    // (node with dataRegex and/or dataType)
    if (!isParsed && reliableNodes.length > 0) {
      let maxScore = 0;
      let bestNode: {
        node: AnalyzerNode;
        index: number;
      };
      let bestResult = {};

      for (const object of reliableNodes) {
        // Reliable node will only consists of DataChunk or DataChunk[] as of now
        object.node = object.node as DataChunk | DataChunk[];

        // Store the temporary key value pair result
        const keyValuePairObject = this.#analyzerNodeTextToKeyValue(textData, object.node);

        if (keyValuePairObject.score > maxScore) {
          maxScore = keyValuePairObject.score;
          bestResult = keyValuePairObject.result;
          bestNode = object;
        }
      }

      if (bestNode && maxScore > 0) {
        Object.assign(this.#parsedObjects, bestResult);
        this.#analyzerGuide.splice(bestNode.index, 1);

        isParsed = true;
      }
    }

    // Check if textData is splittable, if it is, then
    // try to parse with a DataChunk[] node if any
    if (!isParsed && arrayNodes.length > 0) {
      arrayNodes.sort((objA, objB) => {
        objA.node = objA.node as DataChunk[];
        objB.node = objB.node as DataChunk[];

        return objA.node.length - objB.node.length;
      });

      for (const object of arrayNodes) {
        // Array node will only consists of DataChunk[]
        object.node = object.node as DataChunk[];

        // Check if its splittable by 3 spaces at the least
        const splittedTexts = this.#splitTextDataBySpaces(textData, object.node, 5, 3);
        if (splittedTexts.length === object.node.length) {
          const keyValuePairResult = this.#analyzerNodeTextToKeyValue(textData, object.node).result;

          Object.assign(this.#parsedObjects, keyValuePairResult);
          this.#analyzerGuide.splice(object.index, 1);

          isParsed = true;
          break;
        }
      }
    }

    // Parse the textData with the rest of the analyzerGuide if its not parsed yet
    if (!isParsed && (singleNodes.length > 0 || fixedNodes.length > 0)) {
      const analyzerNodes = [...singleNodes, ...fixedNodes];
      analyzerNodes.sort((objA, objB) => objA.index - objB.index);

      const currentNode = analyzerNodes[0];
      const extractionResult = this.extractDataFromText(textData, astIndex, currentNode.index);

      if (extractionResult.isSuccess) {
        this.#analyzerGuide.splice(currentNode.index, 1);
        astIndex = extractionResult.latestASTIndex;
      }

      isParsed = true;
    }

    if (!isParsed) {
      this.#unparsedTexts.push({
        text: textData.trim(),
        lineNumber: this.#lineNumber,
      });
    }

    return astIndex;
  }

  extractDataFromText(textData: string, astIndex: number, analyzerNodeIndex?: number) {
    const nodeIndexExist = !(analyzerNodeIndex === undefined || analyzerNodeIndex === null);
    if (nodeIndexExist) this.#parsedChunks = analyzerNodeIndex;

    let isSuccess = false;

    // Grab current node in analyzerGuide
    let analyzerNode = this.#getCurrentAnalyzerNode;

    // Shortcircuit when analyzerNode is null which means there are
    // no analyzerGuide left to be used for parsing the textData
    if (!analyzerNode) {
      return {
        isSuccess,
        latestASTIndex: -1,
      };
    }

    // Check if current node is a tableChunk, which means that we should now parse
    // all data (each dataChunk of the tableChunk.template) inside the tableChunk
    if (!Array.isArray(analyzerNode) && analyzerNode.chunkType === 'tableChunk') {
      const extractionResult = this.#extractTextWithTableChunk(astIndex, this.#lineNumber);

      // Move on to next analyzer node regardless of whether any table data found
      // previously or not
      if (!nodeIndexExist) this.#parsedChunks += 1;

      if (extractionResult?.isSuccess) {
        Object.assign(this.#parsedObjects, extractionResult.result);
        this.#lineNumber = extractionResult.lineNumber;

        isSuccess = true;
        astIndex = extractionResult.latestASTIndex;
      }
    }

    if (!isSuccess) {
      // analyzerNode will (and should) always be DataChunk or DataChunk[] at this point
      analyzerNode = analyzerNode as DataChunk | DataChunk[];

      // Store the key value pair result for dataChunk
      const keyValuePairResult = this.#analyzerNodeTextToKeyValue(textData, analyzerNode).result;

      const isResultExist = Object.entries(keyValuePairResult).length !== 0;
      if (isResultExist || (!Array.isArray(analyzerNode) && analyzerNode.ignore)) {
        // Move to next analyzer node when current node successfully extracted
        // the data based on the guide or when current node should be ignored
        if (!nodeIndexExist) this.#parsedChunks += 1;
        Object.assign(this.#parsedObjects, keyValuePairResult);

        isSuccess = true;
      }

      if (!isResultExist) {
        this.#unparsedTexts.push({
          text: textData.trim(),
          lineNumber: this.#lineNumber,
        });
      }
    }

    return {
      isSuccess,
      latestASTIndex: astIndex,
    };
  }

  #countAnalyzerNodeLength(analzerNode: DataChunk[]) {
    let total = 0;

    analzerNode.forEach((node) => {
      if (!node.ignore) total += 1;
    });

    return total;
  }

  #findNextDividerPosition(textData: string, separator: string[], spacesAmount = 3) {
    const separatorExist = separator.some((char) => textData.includes(char));
    let isCharFound = false;

    for (const [index, char] of textData.split('').entries()) {
      // Skip spaces in the beginning of the text data
      if (!isCharFound && char !== ' ') {
        isCharFound = true;
      }

      if (isCharFound) {
        if (separatorExist && separator.includes(char)) {
          return index;
        }

        if (char === ' ' && index !== textData.length - 1) {
          if (possiblyDividerText(textData.slice(index, index + spacesAmount), this.#possibleMaxWidth)) {
            // Next [spacesAmount] characters is spaces and indicate a possible divider text
            return index;
          }
        }

        if (index >= textData.length - 1) return index + 1;
      }
    }

    return -1;
  }

  #splitTextDataBySpaces(textData: string, analyzerNode?: DataChunk[], spacesAmount = 3, targetSpacesAmount = 1) {
    const splittedTexts: {
      text: string;
      endPosition: number;
    }[] = [];

    // Split text data using most spaces first algorithm
    while (splittedTexts.length < analyzerNode?.length) {
      const lastSplittedTextPos = splittedTexts.length > 0 ? splittedTexts[splittedTexts.length - 1].endPosition : 0;
      const remainingTextData = textData.slice(lastSplittedTextPos);
      let dividerPos = -1;
      let splittedTextIndex = -1;

      // Find next divider position without taking care of separator (that only consist of spaces)
      if (lastSplittedTextPos >= textData.length - 1) {
        for (const [index, text] of splittedTexts.entries()) {
          dividerPos = this.#findNextDividerPosition(text.text, [], spacesAmount);
          if (dividerPos >= text.text.length - 1) {
            dividerPos = -1;
          }

          if (dividerPos !== -1) {
            splittedTextIndex = index;
            break;
          }
        }
      } else {
        dividerPos = this.#findNextDividerPosition(remainingTextData, [], spacesAmount);
      }

      if (dividerPos === -1 || dividerPos >= textData.length - 1) {
        if (spacesAmount === targetSpacesAmount) {
          break;
        }

        spacesAmount -= 1;
      }

      if (splittedTextIndex === -1) {
        if (dividerPos !== -1) {
          if (splittedTexts.length > 0) {
            dividerPos += lastSplittedTextPos + 1;
          }

          splittedTexts.push({
            text: textData.slice(lastSplittedTextPos, dividerPos),
            endPosition: dividerPos + 1,
          });
        }
      } else {
        const currentSplittedText = splittedTexts[splittedTextIndex];

        splittedTexts[splittedTextIndex] = {
          text: currentSplittedText.text.slice(0, dividerPos),
          endPosition: dividerPos,
        };

        splittedTexts.splice(splittedTextIndex + 1, 0, {
          text: currentSplittedText.text.slice(dividerPos + 1, currentSplittedText.endPosition - 1),
          endPosition: currentSplittedText.endPosition,
        });
      }
    }

    return splittedTexts;
  }

  #splitTextDataBasedOnAnalyzerNode(
    textData: string,
    analyzerNode: DataChunk[],
    spacesAmount = 3,
    splittedTexts: {
      text: string;
      endPosition: number;
    }[] = []
  ): {
    text: string;
    endPosition: number;
  }[] {
    if (splittedTexts.length === 0) {
      splittedTexts = this.#splitTextDataBySpaces(textData, analyzerNode, spacesAmount);
    }

    // Validate each splitted text via its data type and/or data regex
    let nodeIndex = 0;
    while (nodeIndex < analyzerNode.length - 1) {
      const changeTextPlacement = (splittedTextMatchedPos: number, matchedText: string, currentIndex: number) => {
        if (splittedTextMatchedPos === 0) {
          splittedTexts[currentIndex] = {
            text: matchedText,
            endPosition: splittedTexts[currentIndex].endPosition - (splittedTexts[currentIndex].endPosition - matchedText.length),
          };

          if (splittedTexts.length < analyzerNode.length) {
            splittedTexts.splice(currentIndex + 1, 0, {
              text: currentSplittedText.text.slice(matchedText.length),
              endPosition: splittedTexts[currentIndex].endPosition,
            });
          } else {
            if (currentIndex + 1 > splittedTexts.length - 1) {
              // It wont make any sense semantically when this happened so skip current
              // text data instead of trying to make another guess, and let the developer
              // write a better analyzer guide at this node
              throw new Error('Need to push to next data while this is supposedly the last data');
            } else {
              splittedTexts[currentIndex + 1].text = currentSplittedText.text.slice(matchedText.length) + splittedTexts[currentIndex + 1];
            }
          }
        } else {
          splittedTexts[currentIndex].text = matchedText;
          if (splittedTexts.length < analyzerNode.length) {
            splittedTexts.splice(currentIndex - 1, 0, {
              text: currentSplittedText.text.slice(0, splittedTextMatchedPos),
              endPosition: splittedTexts[currentIndex].endPosition - (splittedTexts[currentIndex].endPosition - matchedText.length),
            });
          } else {
            if (currentIndex - 1 < 0) {
              // It wont make any sense semantically when this happened so skip current
              // text data instead of trying to make another guess, and let the developer
              // write a better analyzer guide at this node
              throw new Error('Need to push to previous data while this is supposedly the first data');
            } else {
              splittedTexts[currentIndex - 1] = {
                text: splittedTexts[currentIndex - 1] + currentSplittedText.text.slice(0, splittedTextMatchedPos),
                endPosition:
                  splittedTexts[currentIndex - 1].endPosition +
                  (splittedTexts[currentIndex].endPosition - (splittedTexts[currentIndex].endPosition - matchedText.length)),
              };
            }
          }

          // Recheck previous node
          nextNodeIndex = currentIndex - 1;
        }
      };

      if (nodeIndex > splittedTexts.length - 1) {
        // Cant find any match based on data type nor data regex on current text data,
        // so skip current text data
        throw new Error('Cannot find any matching based on data type nor data regex on current node');
      }

      const node = analyzerNode[nodeIndex];
      const currentSplittedText = splittedTexts[nodeIndex];

      let nextNodeIndex = -1;

      if (node.dataType === 'number' && Number.isNaN(Number(currentSplittedText.text))) {
        for (const [charIndex] of currentSplittedText.text.split('').entries()) {
          const splittedTextMatchedLeft = currentSplittedText.text.slice(0, currentSplittedText.text.length - charIndex);
          const splittedTextMatchedRight = currentSplittedText.text.slice(charIndex);
          const splittedTextMatching = [splittedTextMatchedLeft, splittedTextMatchedRight].filter((el) => el !== '' && !Number.isNaN(Number(el)));

          if (splittedTextMatching.length > 0) {
            const splittedTextMatchedPos = currentSplittedText.text.indexOf(splittedTextMatching[0]);
            changeTextPlacement(splittedTextMatchedPos, splittedTextMatchedPos === 0 ? splittedTextMatchedLeft : splittedTextMatchedRight, nodeIndex);
          }
        }
      }

      // eslint-disable-next-line security/detect-non-literal-regexp
      if (node.dataRegex && !currentSplittedText.text.match(new RegExp(node.dataRegex.body, node.dataRegex.flags || ''))) {
        // eslint-disable-next-line security/detect-non-literal-regexp
        const textDataRegexMatch = textData.match(new RegExp(node.dataRegex.body, node.dataRegex.flags || ''));

        if (!textDataRegexMatch) {
          // Regex cant find any match on current text data, so skip current text data
          throw new Error('Cannot find any matching data on current text data based on node data regex');
        } else {
          let matchedText = '';
          if (textDataRegexMatch.length > 1) {
            matchedText = textDataRegexMatch.slice(1).join('');
          } else {
            matchedText = textDataRegexMatch[0];
          }

          const matchedStartPos = textData.indexOf(matchedText);
          const matchedEndPos = matchedStartPos + matchedText.length;

          let isMatchFound = false;
          for (const [index] of analyzerNode.entries()) {
            const currentSplittedText = splittedTexts[index];

            if (currentSplittedText.text.indexOf(matchedText) !== -1) {
              changeTextPlacement(currentSplittedText.text.indexOf(matchedText), matchedText, nodeIndex);
              isMatchFound = true;
              break;
            }

            if (isMatchFound) break;
          }

          if (!isMatchFound) {
            const splitTextGuide = [
              {
                text: matchedText,
                endPosition: matchedEndPos,
              },
            ];

            if (matchedStartPos > 0) {
              splitTextGuide.unshift({ text: textData.slice(0, matchedStartPos), endPosition: matchedStartPos });
            }

            if (matchedEndPos < textData.length) {
              splitTextGuide.push({ text: textData.slice(matchedEndPos), endPosition: textData.length });
            }

            // No match found for given regex on already splitted text data,
            // possible space(s) exist in the regex, rerun all analysis with
            // guided splitted text based on the regex
            return this.#splitTextDataBasedOnAnalyzerNode(textData, analyzerNode, spacesAmount, splitTextGuide);
          }
        }
      }

      if (nextNodeIndex !== -1) {
        nodeIndex = nextNodeIndex;
        nextNodeIndex = -1;
      } else {
        nodeIndex += 1;
      }
    }

    return splittedTexts;
  }

  #textToKeyValue(
    textData: string,
    separator: string[] | string = undefined
  ): {
    key: string;
    value: string;
  } {
    if (separator && !Array.isArray(separator)) separator = [separator];
    if (!separator || separator.length === 0) separator = [':', '='];

    // separator will always be string[] at this point
    separator = separator as string[];

    let key = null;
    let value = null;

    if (textData) {
      textData = textData.trim();
    } else {
      // Shortcircuit for when textData is null or empty string
      return {
        key,
        value,
      };
    }

    const dividerPos = {
      startPosition: -1,
      endPosition: -1,
    };

    // Try searching with reduced spaces amount when the divider is actually spaces with less than 3 chars
    for (let i = 3; i > 1; i--) {
      dividerPos.startPosition = this.#findNextDividerPosition(textData, separator, i);
      if (dividerPos.startPosition !== -1) break;
    }

    // Reset dividerPos if its on the end of textData
    // (this is due to the findNextDividerPosition fallback value)
    if (dividerPos.startPosition >= textData.length - 1) {
      dividerPos.startPosition = -1;
    }

    // Try searching by getting the minimum character count position that is not
    // a letter and not a number
    if (dividerPos.startPosition === -1) {
      const charCounts: Record<
        string,
        {
          count: number;
          positions: number[];
        }
      > = {};

      for (const [index, char] of textData.split('').entries()) {
        // Check if its not a letter and not a number, then its probably a divider
        if (char.toLowerCase() === char.toUpperCase() && Number.isNaN(Number(char))) {
          if (char in charCounts) {
            charCounts[char].count += 1;
            charCounts[char].positions.push(index);
          } else {
            charCounts[char] = {
              count: 0,
              positions: [index],
            };
          }
        }
      }

      if (Object.entries(charCounts).length > 0) {
        let minimumCount = Infinity;
        for (const el of Object.values(charCounts)) {
          if (el.count < minimumCount) minimumCount = el.count;
        }

        let minimumCountCharPositions: number[] = [];
        for (const [, el] of Object.entries(charCounts)) {
          if (el.count === minimumCount) {
            minimumCountCharPositions.push(...el.positions);
          }
        }

        minimumCountCharPositions = minimumCountCharPositions.sort((a, b) => a - b);
        dividerPos.startPosition = minimumCountCharPositions[0];

        // Multiple possible divider found, merge those if possible
        if (minimumCountCharPositions.length > 1) {
          for (const [index, position] of minimumCountCharPositions.entries()) {
            if (index !== minimumCountCharPositions.length - 1) {
              const offsetToLastPosition = Math.abs(position - minimumCountCharPositions[index + 1]);

              if (offsetToLastPosition > 1) {
                dividerPos.startPosition = position;
              } else {
                dividerPos.endPosition = position;

                // If next data is last data
                if (index + 1 >= minimumCountCharPositions.length - 1) {
                  dividerPos.endPosition = minimumCountCharPositions[index + 1];
                }
              }
            }
          }
        }
      }
    }

    if (dividerPos.startPosition !== -1) {
      key = textData.slice(0, dividerPos.startPosition).trim().toLowerCase().replace(' ', '-');
      value = textData.slice(dividerPos.endPosition === -1 ? dividerPos.startPosition + 1 : dividerPos.endPosition + 1).trim();
    }

    return {
      key: key || null,
      value: value || null,
    };
  }

  #extractTextWithTableChunk(skipASTUntilIndex: number, lineNumber: number) {
    // Current node will (and should) always be a TableChunk here
    const analyzerNode = this.#getCurrentAnalyzerNode as TableChunk;

    // Store each character position in the title of a tableChunk to help
    // analyze each data in the tableChunk with the position based algorithm
    let titleCharacterPositions = [];

    // The resulting data of the parsed tableChunk
    const parsedTableData: Record<string, any>[] = [];

    // The temporary result of data in a tableChunk, this is useful for when
    // each data in a table actually consists of multiple lines instead of one
    let tempParsedTableData: Record<string, any>[][] = [[]];

    const extractionResult = {
      result: {},
      isSuccess: false,
      latestASTIndex: -1,
      lineNumber: this.#lineNumber,
    };

    for (const [astIndex, node, textData] of walkAST(this.#ast, new PrinterContext())) {
      if (isPrintingWithLineFeedCommand(node.command)) lineNumber += 1;

      // Skip parsing ast node until the target index
      if (skipASTUntilIndex > astIndex) continue;

      // Skip parsing divider text automatically so no need to put this into analyzerGuide
      if (textData.length === 0 || possiblyDividerText(textData, this.#possibleMaxWidth)) continue;

      // Current line of each data in the table
      const currentTableLine = tempParsedTableData[tempParsedTableData.length - 1].length % analyzerNode.template.length;

      // The template of currentTableLine
      const currentLineTemplate: DataChunk[] = analyzerNode.template[currentTableLine];

      // Store the title character position of the current line just
      // in case the tableChunk have multiple lines of title too
      const currentTitleCharacterPositions = titleCharacterPositions[currentTableLine];

      // Whether the tableChunk is successfully parsed or not
      let successfullyParsed = false;

      // Whether we should skip parsing the rest of data in
      // tableChunk and go to next node, this is for when
      // the tableChunk.endsWith has been found
      let shouldGoToNextNode = false;

      // Run semantic analysis if a tableChunk have a titleTemplate by checking the
      // position matching of character of the value texts with the title texts
      if (analyzerNode.titleTemplate && analyzerNode.titleTemplate.length > 0) {
        // Keep parsing current line of textual data as tableChunk title
        if (titleCharacterPositions.length < analyzerNode.titleTemplate.length) {
          try {
            // Get the title character positions
            titleCharacterPositions.push(textData.split('').map((char, index) => (char !== ' ' ? [index] : [null])));

            const splittedTexts = this.#splitTextDataBasedOnAnalyzerNode(textData, analyzerNode.titleTemplate[currentTableLine]);
            let charIndex = 0;

            // Combine title character positions based on splittedTexts
            for (const [splittedTextIndex, splittedTextData] of splittedTexts.entries()) {
              const indexOfSplittedText = textData.slice(0, splittedTextData.endPosition).lastIndexOf(splittedTextData.text.trim());
              for (let i = charIndex; i < splittedTextData.endPosition; i++) {
                if (indexOfSplittedText <= i && i <= splittedTextData.endPosition && i <= textData.length - 1) {
                  titleCharacterPositions[titleCharacterPositions.length - 1][i].push(splittedTextIndex);
                  if (titleCharacterPositions[titleCharacterPositions.length - 1][i][0] === null) {
                    titleCharacterPositions[titleCharacterPositions.length - 1][i][0] = i;
                  }
                }
              }

              charIndex = splittedTextData.endPosition;
            }

            successfullyParsed = true;
          } catch {
            // Current line doesnt match so reset the title positions
            if (titleCharacterPositions.length > 0) {
              titleCharacterPositions = [];
            }

            // Failed parsing the title of current tableChunk so fallback to
            // other analysis method or go to next node later in the process
            successfullyParsed = false;
          }
        }
        // Parse data inside tableChunk after title have been successfully parsed
        else {
          // eslint-disable-next-line security/detect-non-literal-regexp
          if (analyzerNode.endsWith && textData.match(new RegExp(analyzerNode.endsWith.body, analyzerNode.endsWith.flags || ''))) {
            // Ending indicator found so go to next node
            shouldGoToNextNode = true;
          }

          let currentTextMatching: {
            text: string;
            endPosition: number;
            isMatched: boolean;
          }[] = [];

          // Get matching character position of value data with the title character positions
          let currentTextPositions = [];
          for (const [index, character] of textData.split('').entries()) {
            const isLastData = index === textData.length - 1;
            if (character === ' ' || isLastData) {
              if (isLastData && character !== ' ') {
                currentTextPositions.push(index);
              }

              if (currentTextPositions.length > 0) {
                let matchedPos = -1;
                for (const [index, [titleCharPos]] of currentTitleCharacterPositions.entries()) {
                  const isPrevPosNull = index === 0 || currentTitleCharacterPositions[index - 1][0] === null;
                  const isNextPosNull = index === currentTitleCharacterPositions.length - 1 || currentTitleCharacterPositions[index + 1][0] === null;

                  // Current text position matches the first character in a title text
                  if (currentTextPositions[0] === titleCharPos && isPrevPosNull) {
                    matchedPos = index;
                    break;
                  }

                  // Current text position matches the last character in a title text
                  if (currentTextPositions[currentTextPositions.length - 1] === titleCharPos && isNextPosNull) {
                    matchedPos = index;
                    break;
                  }

                  // Current text position is inside a title text (from left side)
                  const midMatchingFromLeft = currentTitleCharacterPositions[currentTextPositions[0]]?.[0];
                  if (midMatchingFromLeft !== null) {
                    matchedPos = midMatchingFromLeft;
                    break;
                  }

                  // Current text position is inside a title text (from right side)
                  const midMatchingFromRight = currentTitleCharacterPositions[currentTextPositions[currentTextPositions.length - 1]][0];
                  if (midMatchingFromRight !== null) {
                    matchedPos = midMatchingFromRight;
                    break;
                  }
                }

                let shouldCreateNewMatching = true;
                let titleNodeIndex = -1;

                if (matchedPos !== -1) {
                  titleNodeIndex = currentTitleCharacterPositions[matchedPos]?.[1];

                  if (currentTextMatching[titleNodeIndex]) {
                    const matchedText = textData.slice(currentTextMatching[titleNodeIndex].endPosition, isLastData ? index + 1 : index);
                    currentTextMatching[titleNodeIndex].text += matchedText;
                    currentTextMatching[titleNodeIndex].endPosition += matchedText.length;
                    shouldCreateNewMatching = false;
                  }
                }

                if (shouldCreateNewMatching) {
                  if (titleNodeIndex !== -1) {
                    currentTextMatching[titleNodeIndex] = {
                      text: textData.slice(currentTextPositions[0], isLastData ? index + 1 : index),
                      endPosition: index,
                      isMatched: true,
                    };
                  } else {
                    currentTextMatching.push({
                      text: textData.slice(currentTextPositions[0], isLastData ? index + 1 : index),
                      endPosition: index,
                      isMatched: false,
                    });
                  }
                }

                currentTextPositions = [];
              }

              continue;
            }

            currentTextPositions.push(index);
          }

          if (currentTextMatching.some((el) => el.isMatched)) {
            // This will be false whenever there is a non matched text
            let matchedTextFound = currentTextMatching.every((el) => el.isMatched);

            if (currentTextMatching.some((el) => !el.isMatched)) {
              const tempCurrentTextMatching: {
                text: string;
                endPosition: number;
                isMatched: boolean;
              }[] = [];
              let tempNonMatchedText = '';

              // Merge other non matched text with the first closest neighbor of matched text
              for (const [index, matching] of currentTextMatching.entries()) {
                if (matching?.isMatched) {
                  tempCurrentTextMatching.push(matching);
                  matchedTextFound = true;
                } else {
                  if (!matchedTextFound) {
                    // If there are any non matched text in the first datas
                    // then break early to run other analysis instead
                    break;
                  }

                  if (matching) {
                    const slicingEndPosition = matching.endPosition === textData.length - 1 ? matching.endPosition + 1 : matching.endPosition;
                    tempNonMatchedText += textData.slice(currentTextMatching[index - 1].endPosition, slicingEndPosition);

                    if (index === currentTextMatching.length - 1 || currentTextMatching[index + 1].isMatched) {
                      tempCurrentTextMatching[tempCurrentTextMatching.length - 1].text += tempNonMatchedText;
                      tempCurrentTextMatching[tempCurrentTextMatching.length - 1].endPosition = matching.endPosition;
                      tempNonMatchedText = '';
                    }
                  }
                }
              }

              currentTextMatching = tempCurrentTextMatching;
            }

            if (matchedTextFound) {
              if (currentTextMatching.length !== currentLineTemplate.length) {
                // Fill in the text matching with some empty data instead
                currentTextMatching.push(undefined);
              }

              const tempMergedObject = {};
              let isAllValueCorrect = true;
              for (const [index, matching] of currentTextMatching.entries()) {
                const currentAnalyzerNode = currentLineTemplate[index] as DataChunk;
                const objResult = this.#analyzerNodeTextToKeyValue(matching?.text, currentAnalyzerNode);

                if (Object.entries(objResult.result).length === 0) {
                  isAllValueCorrect = false;
                  break;
                }

                // Value match dataType and dataRegex if any, so
                // return the key value pair if we should not ignore it
                if (!currentAnalyzerNode.ignore) {
                  Object.assign(tempMergedObject, objResult.result);
                }
              }

              if (isAllValueCorrect) {
                tempParsedTableData[currentTableLine].push(tempMergedObject);
                successfullyParsed = true;
              }
            } else {
              shouldGoToNextNode = true;
            }
          }
        }
      }

      // eslint-disable-next-line security/detect-non-literal-regexp
      if (analyzerNode.endsWith && textData.match(new RegExp(analyzerNode.endsWith.body, analyzerNode.endsWith.flags || ''))) {
        // Ending indicator found so go to next node
        shouldGoToNextNode = true;
      }

      /**
       * Use general dataChunk analysis for when
       *
       * 1. the tableChunk doesnt have any titleTemplate, or
       * 2. tableChunk position matching analysis failed
       */
      if (!successfullyParsed && !shouldGoToNextNode) {
        const tableData = this.#analyzerNodeTextToKeyValue(textData, currentLineTemplate).result;
        if (Object.entries(tableData).length === this.#countAnalyzerNodeLength(currentLineTemplate)) {
          if (currentTableLine > tempParsedTableData.length - 1) {
            tempParsedTableData.push([tableData]);
          } else {
            tempParsedTableData[currentTableLine].push(tableData);
          }
          successfullyParsed = true;
        }
      }

      // Merge all parsed data from current text data to the whole table data
      if (tempParsedTableData.length === analyzerNode.template.length && tempParsedTableData.every((el) => el.length > 0)) {
        const tempMergedParsedTableData = {};
        for (const tempParsedTable of tempParsedTableData) {
          Object.assign(tempMergedParsedTableData, ...tempParsedTable);
        }

        parsedTableData.push(tempMergedParsedTableData);
        tempParsedTableData = [[]];
      }

      if (!successfullyParsed || shouldGoToNextNode) {
        if (parsedTableData.length > 0) {
          extractionResult.isSuccess = true;
          extractionResult.lineNumber = lineNumber;

          // Inject the table data for when it no longer finds
          // any table value data / rows
          extractionResult.result = { [analyzerNode.node]: parsedTableData };

          // Inject the current astIndex as the latest parsed AST Index
          extractionResult.latestASTIndex = astIndex;
        }

        return extractionResult;
      }
    }

    // Handle edge case for when the table data is parsing
    // until the end of the text data or the AST
    if (parsedTableData.length > 0) {
      extractionResult.isSuccess = true;
      extractionResult.lineNumber = lineNumber;

      // Inject the table data for when it no longer finds
      // any table value data / rows
      extractionResult.result = { [analyzerNode.node]: parsedTableData };

      // Set latest parsed AST index as Infinity to make sure
      // the analysis stops in the parent loops as it has
      // successfully parsed until the end of the AST
      extractionResult.latestASTIndex = Infinity;
    }

    return extractionResult;
  }

  // Text to key value wrapper for analyzer node
  #analyzerNodeTextToKeyValue(
    textData: string,
    analyzerNode: AnalyzerNode
  ): {
    result: Record<string, any>;
    score: number;
  } {
    let objResult = {};
    let matchingScore = 0;

    if (textData) {
      textData = textData.trim();
    }

    if (Array.isArray(analyzerNode)) {
      analyzerNode = analyzerNode as DataChunk[];
      try {
        const splittedTexts = this.#splitTextDataBasedOnAnalyzerNode(textData, analyzerNode);

        // Assign resulting object of key value pair from each splitted text
        for (const [index, node] of analyzerNode.entries()) {
          const object = this.#analyzerNodeTextToKeyValue(splittedTexts[index]?.text ?? '', node);
          if (Object.entries(object.result).length > 0 || node.ignore) {
            matchingScore += object.score;

            if (!node.ignore) Object.assign(objResult, object.result);
          }
        }
      } catch (error) {
        console.log(`[Warning]: ${error}`);
        objResult = {};
      }
    } else if (analyzerNode?.chunkType === 'dataChunk') {
      analyzerNode = analyzerNode as DataChunk;

      const value = (() => {
        if (analyzerNode.isKeyValuePair) {
          return this.#textToKeyValue(textData, analyzerNode.divider || []).value;
        } else {
          return textData;
        }
      })();

      let tempResult = {
        [analyzerNode.node]: value || null,
      };

      if (!value && !analyzerNode.isEmptyable) {
        // Value should not be empty
        tempResult = {};
      }

      if (value) {
        if (analyzerNode.dataType === 'number' && Number.isNaN(Number(value))) {
          // Value doesnt match given dataType in the analyzerNode
          tempResult = {};
        }

        // eslint-disable-next-line security/detect-non-literal-regexp
        if (analyzerNode.dataRegex && !value.match(new RegExp(analyzerNode.dataRegex.body, analyzerNode.dataRegex.flags || ''))) {
          // Value doesnt match given dataRegex in the analyzerNode
          tempResult = {};
        }
      }

      // dataType were used in the analyzerNode so the result is more reliable
      if ('dataType' in analyzerNode && Object.entries(tempResult).length > 0) {
        matchingScore += 2;
      }

      // dataRegex were used in the analyzerNode so the result is even more reliable
      if ('dataRegex' in analyzerNode && Object.entries(tempResult).length > 0) {
        matchingScore += 4;
      }

      // Value match dataType and dataRegex if any, so
      // return the key value pair if we should not ignore it
      if (!analyzerNode.ignore) {
        objResult = tempResult;
      }
    }

    return {
      result: objResult,
      score: matchingScore,
    };
  }
}
